'use client'

import React, { FC, ReactElement } from 'react'
import axios from 'axios'
import useSwr from 'swr'
import classNames from 'classnames'
import { Input } from '@/components/ui/input'
import { CommandItem } from '@/components/ui/command'
import formatNumber from '@/utils/format-number'
import { formatSatoshis } from '@/utils/hooks/useSatoshiPrice'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import { NETWORK, API_URL } from '@/utils/constants'
import Wallet from '@/utils/wallet'
import { useToast } from '@/components/ui/use-toast'
import featureFlags from '@/utils/feature-flags'
import useWallet from '@/utils/hooks/useWallet'
import InscriptionContent from '@/components/inscription-content'
import { Loading } from '@/components/ui/loading'
import { Inscription } from '@/utils/types/inscription'

import useSelect from '../hooks/useSelect'
import SelectButton from '../components/select-button'
import Invoice from '../components/invoice'
import { protocolFromInscription } from '@/utils/market/secure-purchase'
import { useSnipeProtection } from '@/utils/market/use-snipe-protection'
import { snipeProtectionInvoiceItem } from '@/components/snipe-protection-toggle'
import {
	describeSecureListingResult,
	SNIPE_UNAVAILABLE_CODES,
	isolateTargetForFailures,
	listSecureItems
} from '@/utils/market/secure-listing-client'
import { toastListingSuccess } from '@/utils/market/listing-success-toast'

interface ExternalListing {
	id: string
	collection: string
	price: number
	owner: string
	output: string
}

interface JoinedListing {
	listing: ExternalListing
	inscription: Inscription
}

const ImportListings: FC<{ onNavigate: (v: string) => void; onClose: () => void; args: any }> = (
	props
): ReactElement => {
	const { onClose } = props
	const { toast } = useToast()
	const { exchangeRate } = useExchangeRate()
	const address = Wallet.getTaprootAddress()
	const { data: walletData, isLoading: walletLoading } = useWallet()

	const [error, setError] = React.useState<string | null>(null)
	const [prices, setPrices] = React.useState<Record<string, number>>({})

	// SWR keyed on address — null key suspends until address is set.
	const { data: importListingsResp, isLoading: fetchLoading } = useSwr(
		address ? `${API_URL}/wallet/${address}/import-listings` : null,
		async (url: string) => {
			try {
				const { data } = await axios.get(url)
				return data
			} catch (e: any) {
				setError(e?.response?.data?.message || e?.message || 'Failed to fetch listings')
				return null
			}
		},
		{ revalidateOnFocus: false }
	)
	const externalListings: ExternalListing[] = React.useMemo(
		() => importListingsResp?.listings || [],
		[importListingsResp]
	)
	const walletInscriptions = walletData?.inscriptions

	// Join external listings against wallet inscriptions — only keep items we actually own
	const items = React.useMemo(() => {
		if (!walletInscriptions || !externalListings.length) return []

		const inscriptionMap = new Map<string, Inscription>()
		for (const insc of walletInscriptions) {
			inscriptionMap.set(insc.id, insc)
		}

		const joined: JoinedListing[] = []
		for (const listing of externalListings) {
			const insc = inscriptionMap.get(listing.id)
			if (insc) {
				joined.push({ listing, inscription: insc })
			}
		}
		return joined
	}, [walletInscriptions, externalListings])

	// Set initial prices once items are resolved
	React.useEffect(() => {
		if (!items.length) return
		const initialPrices: Record<string, number> = {}
		for (const { listing } of items) {
			initialPrices[listing.id] = listing.price / 1e8
		}
		requestAnimationFrame(() => {
			setPrices(initialPrices)
		})
	}, [items])

	const ids = React.useMemo(() => items.map((e) => e.listing.id), [items])
	const { selected, setSelected, onSelect, onSelectAll, allSelected } = useSelect(ids)

	React.useEffect(() => {
		if (ids.length > 0) {
			requestAnimationFrame(() => {
				setSelected(new Set(ids))
			})
		}
	}, [ids, setSelected])

	const handlePriceChange = React.useCallback(
		(id: string) => (evt: any) => {
			const value = evt.target.value
			setPrices((prev) => ({ ...prev, [id]: parseFloat(value) || 0 }))
		},
		[]
	)

	const { selectedCount, totalPrice, totalPriceUsd } = React.useMemo(() => {
		let total = 0
		let count = 0

		for (const [id] of Array.from(selected.entries())) {
			total += (prices[id] || 0) * 1e8
			count++
		}

		return {
			selectedCount: count,
			totalPrice: formatSatoshis(total),
			totalPriceUsd: formatNumber(((total / 1e8) * exchangeRate)?.toFixed(2))
		}
	}, [selected, prices, exchangeRate])

	// Imports span collections; only an unrestricted rollout offers protection here.
	const snipeProtection = useSnipeProtection('ordinal', null)

	const handleSubmit = React.useCallback(async () => {
		try {
			const selectedIds: string[] = []
			const finalPrices: number[] = []

			for (const [id] of Array.from(selected.entries())) {
				selectedIds.push(id)
				finalPrices.push(parseInt(((prices[id] || 0) * 1e8).toFixed(0)))
			}

			const selectedItems = items.filter(({ listing }) => selected.has(listing.id))
			const listingTarget = {
				inscriptionIds: selectedItems.map((item) => item.inscription?.id || item.listing.id),
				collectionSlugs: selectedItems.map(
					(item) => item.inscription?.collection?.slug || item.listing.collection
				)
			}

			let secureResult: Awaited<ReturnType<typeof listSecureItems>> | null = null
			if (snipeProtection.enabled) {
				try {
					secureResult = await listSecureItems(
					selectedIds.map((id, index) => ({
						inscriptionId: id,
						priceSats: finalPrices[index],
						protocol: protocolFromInscription(
							items.find((row) => row.listing.id === id)?.inscription || {}
						)
					}))
				)
				} catch (e: any) {
					// Protection unavailable must not block the sale: list standard.
					if (!SNIPE_UNAVAILABLE_CODES.has(e?.code)) throw e
					toast({
						title: 'Listing without snipe protection',
						description:
							'Snipe protection is unavailable right now, so this listing is being made the standard way.'
					})
				}
			}
			if (secureResult) {
				const result = secureResult
				const summary = describeSecureListingResult(result, 'inscriptions')
				toastListingSuccess({
					title: summary.title,
					description: summary.description,
					...(summary.failed ? { variant: 'destructive' as const } : {}),
					isolate: isolateTargetForFailures(
						result.failures,
						selectedIds.map((id) => {
							const row = items.find((item) => item.listing.id === id)
							return {
								inscriptionId: row?.inscription?.id || id,
								// The indexer row carries its current outpoint; the typed shape does not.
								outpoint: (row?.inscription as { outpoint?: string } | undefined)?.outpoint
							}
						}),
						listingTarget.collectionSlugs?.[0] ?? null
					),
					count: result.listed,
					...listingTarget
				})
				if (!summary.failed) onClose()
				return
			}

			const psbt = await Wallet.getEscrows(selectedIds, finalPrices, {
				forceBulk: featureFlags('app::disable_bulk')
			})

			let totalCreatedEscrows = 0
			let totalSkippedInputs = 0
			let skippedInputs: any[] = []

			if (Array.isArray(psbt)) {
				for (const e of psbt) {
					const { rawtx } = await Wallet.signEscrow(e)
					const { data: resData } = await axios.post(`${API_URL}/market/escrow-bulk`, {
						psbt: rawtx
					})
					const summary = Wallet.assertEscrowResponse(resData)
					totalCreatedEscrows += summary.createdEscrows
					totalSkippedInputs += summary.skippedInputsCount
					skippedInputs = skippedInputs.concat(summary.skippedInputs || [])
				}
			} else {
				const { rawtx } = await Wallet.signEscrow(psbt)
				const { data: resData } = await axios.post(`${API_URL}/market/escrow-bulk`, {
					psbt: rawtx
				})
				const summary = Wallet.assertEscrowResponse(resData)
				totalCreatedEscrows += summary.createdEscrows
				totalSkippedInputs += summary.skippedInputsCount
				skippedInputs = skippedInputs.concat(summary.skippedInputs || [])
			}

			if (totalCreatedEscrows <= 0) {
				throw new Error('No escrows were created')
			}

			if (totalSkippedInputs > 0) {
				const skippedSummary = Wallet.formatEscrowSkips(skippedInputs)
				toastListingSuccess({
					title: 'Partially Listed',
					description: `Created ${formatNumber(
						totalCreatedEscrows
					)} escrows. Skipped ${formatNumber(totalSkippedInputs)} inputs${
						skippedSummary ? `: ${skippedSummary}` : '.'
					}.`,
					count: totalCreatedEscrows,
					...listingTarget
				})
			} else {
				toastListingSuccess({
					title: 'Success!',
					description: `You listed ${formatNumber(selectedCount)} inscriptions`,
					count: selectedCount,
					...listingTarget
				})
			}
			onClose()
		} catch (e: any) {
			toast({
				title: 'Error',
				description: e?.response?.data?.message || e?.message || 'Unable to list item',
				variant: 'destructive'
			})
		}
	}, [onClose, selected, prices, toast, selectedCount, items, snipeProtection.enabled])

	const isDisabledForNoPrice = totalPrice === '0'

	const renderListing = React.useCallback(
		({ listing, inscription }: JoinedListing) => {
			const isSelected = selected.has(listing.id)
			const price = prices[listing.id]

			return (
				<CommandItem
					key={listing.id}
					value={listing.id}
					onSelect={() => onSelect(listing.id)}
					className="data=[cmdk-item]:py-0.5"
				>
					<div className="flex justify-between w-full">
						<div className="flex gap-2 items-center">
							<SelectButton onSelect={() => onSelect(listing.id)} selected={isSelected} />
							<InscriptionContent inscription={inscription} className="size-6" />
							{inscription?.meta?.name || inscription?.collection?.name || `#${inscription?.num}`}
						</div>
						<div className="flex gap-2 items-center">
							<Input
								value={!isSelected || !price ? '' : price}
								placeholder="--"
								onClick={(evt) => isSelected && evt.stopPropagation()}
								onKeyDown={(evt) => isSelected && evt.stopPropagation()}
								onChange={handlePriceChange(listing.id)}
								type="number"
								step={0.0001}
								className={classNames(
									'text-xs py-0 px-2 h-7 w-24 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
									{ ['text-muted-foreground']: !price }
								)}
							/>
						</div>
					</div>
				</CommandItem>
			)
		},
		[selected, prices, onSelect, handlePriceChange]
	)

	const loading = fetchLoading || walletLoading

	if (loading) {
		return (
			<div className="flex justify-center pt-8">
				<Loading size={16} />
			</div>
		)
	}

	if (error) {
		return <p className="text-xs text-red-500 text-center pt-8">{error}</p>
	}

	if (items.length === 0) {
		return (
			<p className="text-xs text-muted-foreground text-center pt-8">No external listings found.</p>
		)
	}

	return (
		<>
			<div className="flex justify-between items-center px-2 py-3">
				<div className="flex gap-2 items-center">
					<SelectButton selected={allSelected} onSelect={onSelectAll} />
					<p className="text-xs text-muted-foreground select-none">
						{items.length} external listing{items.length !== 1 ? 's' : ''}
					</p>
				</div>
				<p className="text-xs text-muted-foreground select-none">Price ({NETWORK})</p>
			</div>
			{items.map(renderListing)}
			<Invoice
				open={selected.size > 0}
				onSubmit={handleSubmit}
				disabled={isDisabledForNoPrice}
				action={
					isDisabledForNoPrice
						? 'Please set a price'
						: `List ${formatNumber(selectedCount)} inscriptions`
				}
				items={[
					{ title: 'You Receive', value: `${totalPrice} ${NETWORK} / $${totalPriceUsd}` },
					...snipeProtectionInvoiceItem(snipeProtection.offered)
				]}
			/>
		</>
	)
}

export default ImportListings
